import { FlaskConical, Send } from "lucide-react";
import * as React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useArtifacts } from "@/api/artifacts";
import { ApiError } from "@/api/client";
import { useUploadMolecule } from "@/api/molecules";
import {
  useFlowDag,
  useFlowDagOverlay,
  type DagNodeWithState,
  type FlowDagOverlayResponse,
  type FlowDagResponse,
} from "@/api/dag";
import { useDryRunFlow, useFlowManifest, useFlows, useSubmitFlow } from "@/api/flows";
import { useProject } from "@/api/projects";
import { useExecutionMode } from "@/api/readyz";
import { BulkRetryControl } from "@/components/dag/BulkRetryControl";
import { derivePhaseLane } from "@/components/dag/phase-lane";
import { FlowShapeSwitch, PhaseLane, type FlowShapeView } from "@/components/dag/PhaseLane";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { FlowSubmittedNotice } from "@/components/projects/FlowSubmittedNotice";
import { ArtifactInputPicker, type InputPort } from "@/components/submission/ArtifactInputPicker";
import { ExecutionModeNotice } from "@/components/submission/ExecutionModeNotice";
import { SelectedInputPreview } from "@/components/submission/SelectedInputPreview";
import { SubmitReadiness, type CompileVerdict } from "@/components/submission/SubmitReadiness";
import { FlowManifestPreview } from "@/components/submission/FlowManifestPreview";
import { FlowPicker } from "@/components/submission/FlowPicker";
import {
  ManifestForm,
  declaredParameterDefaults,
  renderableParameterEdits,
} from "@/components/submission/ManifestForm";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { VisualPanel } from "@/components/viz/VisualPanel";

// Lazy-loaded so reactflow (~150 KB gz) only lands when a user
// opens the DAG tab, not on every Flow submission page load.
const DAGCanvas = React.lazy(() =>
  import("@/components/dag/DAGCanvas").then((m) => ({ default: m.DAGCanvas }))
);

interface RouteParams {
  slug?: string;
  [key: string]: string | undefined;
}

/**
 * Flow submission: picker → preview → inputs → submit (EPIC-001 U6).
 *
 * Two-column layout on desktop: Flow picker (left) → manifest preview +
 * DAG + input wiring (right).
 *
 * Submitting POSTs to /api/flows/submit, which compiles the template and
 * dispatches the DAG in the background — one run per fan-out child. There
 * is no flow-run id to navigate to (FlowStack has no flow-run table), so
 * the page stays put: the DAG below it is the live view, and its overlay
 * colors each node as its children reach terminal states.
 *
 * Flow inputs are catalog artifacts, referenced by content_hash — the same
 * currency the Tile page uses. A first Molecule can be seeded two ways with
 * identical semantics: the SDK (`client.types.Molecule.from_sdf(...)`) or the
 * picker's own SDF upload control (POST /api/molecules → seed_molecule_from_sdf,
 * dispatch 039). The picker then references the catalogued artifact by hash.
 *
 * Flow PARAMETERS are set through the same `ManifestForm` the Tile page uses
 * (#831 / request 064). Until then this page only *previewed* them, so
 * `full_affdo_sequential` rendered a full description of `reference_level`
 * beside no control to set it and every run silently took the declared
 * defaults. The preview stays — it carries the long descriptions — but it is
 * no longer the only place a parameter appears.
 */
export function SubmitFlowPage(): JSX.Element {
  const { slug = "" } = useParams<RouteParams>();
  const project = useProject(slug);
  const flows = useFlows();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFlow = searchParams.get("flow");
  const manifest = useFlowManifest(selectedFlow ?? "");
  const artifacts = useArtifacts();
  const submit = useSubmitFlow();
  const dryRun = useDryRunFlow();
  const uploadMolecule = useUploadMolecule();
  // What this deployment can actually do with a submission (#1174). Read
  // before anything is submitted, and never used to gate the Submit button.
  const executionMode = useExecutionMode();
  const [selectedArtifacts, setSelectedArtifacts] = React.useState<Record<string, string>>({});
  // The raw record of what the user touched. It is deliberately NOT the
  // dict the form reads or the request body is built from: both of those
  // are `renderableEdits` below, which reconciles this against whatever
  // manifest is current. Untouched parameters come from the manifest's
  // declared defaults at submit time.
  const [parameterEdits, setParameterEdits] = React.useState<Record<string, unknown>>({});

  // Swapping flows must not carry the previous flow's input selections
  // or parameter edits over — the port and parameter names (and their
  // types) are different.
  React.useEffect(() => {
    setSelectedArtifacts({});
    setParameterEdits({});
  }, [selectedFlow]);

  // Flow-level declared inputs, defensively shaped (same posture as the
  // Tile page, Rupert PR-300 r0): an entry without a string `type` is
  // dropped rather than rendered as an untyped picker. Every declared flow
  // input is required — FlowSchema.FlowInput has no `required` flag, and
  // the compiler needs a value for each to resolve its bindings.
  const flowInputs = React.useMemo(() => {
    const raw = (manifest.data?.manifest as { inputs?: unknown } | undefined)?.inputs;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
    const out: Record<string, InputPort> = {};
    for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
      if (!value || typeof value !== "object") continue;
      const v = value as { type?: unknown; description?: unknown };
      if (typeof v.type !== "string") continue;
      out[name] = {
        type: v.type,
        cardinality: "one",
        required: true,
        description: typeof v.description === "string" ? v.description : undefined,
      };
    }
    return out;
  }, [manifest.data]);

  // The user's edits reconciled against the CURRENT manifest — the one
  // dict both the form and the request body are built from, which is what
  // makes "what you see" and "what you submit" the same object rather
  // than two derivations that can disagree.
  //
  // `parameterEdits` is cleared on flow swap but survives a same-flow
  // manifest refetch, so a template redeployed under the open page can
  // leave an edit for a parameter it no longer exposes (Rupert PR-841 r1)
  // or has re-typed under (r2) — and an emptied numeric field leaves a
  // `null` the control does not display either. `renderableParameterEdits`
  // drops exactly those; the form then falls back to the declared default
  // for them, and so does the submission. Feeding this back through
  // ManifestForm's `values` also prunes the dropped entries from state on
  // the user's next edit, so nothing lingers invisibly.
  const renderableEdits = React.useMemo(
    () => renderableParameterEdits(manifest.data?.manifest, parameterEdits),
    [manifest.data, parameterEdits]
  );

  // Every settable parameter at its declared default, overlaid with the
  // reconciled edits. Submitting THIS — rather than only the edits — makes
  // the request say exactly what the user was looking at when they pressed
  // Submit, instead of relying on the server re-deriving defaults from a
  // template that may have been redeployed since the manifest was fetched.
  const submittedParameters = React.useMemo(
    () => ({ ...declaredParameterDefaults(manifest.data?.manifest), ...renderableEdits }),
    [manifest.data, renderableEdits]
  );

  const missingRequiredInputs = React.useMemo(() => {
    if (!flowInputs) return [];
    return Object.keys(flowInputs).filter((name) => !selectedArtifacts[name]);
  }, [flowInputs, selectedArtifacts]);

  // The exact request the dry run's verdict is ABOUT (flowstack#1398). A dry
  // run answers a question about one body; the moment the reader changes an
  // input or a parameter it is answering a question they are no longer asking,
  // and a stale "15 nodes compile" is worse than no line at all — it is the
  // page vouching for a submission nobody compiled. So the verdict is keyed to
  // the body and discarded when the key moves.
  const dryRunSubject = React.useMemo(
    () => JSON.stringify([selectedFlow, selectedArtifacts, submittedParameters]),
    [selectedFlow, selectedArtifacts, submittedParameters]
  );
  const { reset: resetDryRun } = dryRun;
  React.useEffect(() => {
    resetDryRun();
  }, [dryRunSubject, resetDryRun]);
  // Prefer the overlay endpoint so live run state colors the canvas;
  // fall back to the structural endpoint only when overlay errors
  // (e.g. no database wired). Gating ``useFlowDag`` on the overlay
  // error avoids firing two endpoint hits — each of which re-walks
  // the flows directory — on the happy path. The DAGCanvas accepts
  // either shape.
  const overlay = useFlowDagOverlay(selectedFlow ?? "");
  const structural = useFlowDag(selectedFlow ?? "", { enabled: overlay.isError });
  const dag = overlay.isError ? structural : overlay;

  if (project.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (project.isError) {
    const isNotFound = project.error instanceof ApiError && project.error.status === 404;
    return (
      <EmptyState
        title={isNotFound ? "Project not found." : "Could not load project."}
        description={
          isNotFound
            ? `No project with slug ${slug ? `'${slug}'` : "(empty)"}.`
            : project.error instanceof Error
              ? project.error.message
              : "Unknown error."
        }
      />
    );
  }
  if (!project.data) return <></>;

  const onSelectFlow = (name: string): void => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("flow", name);
      return params;
    });
  };

  const onSelectArtifact = (portName: string, contentHash: string | undefined): void => {
    setSelectedArtifacts((prev) => {
      if (contentHash === undefined) {
        const { [portName]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [portName]: contentHash };
    });
  };

  const onDryRun = (): void => {
    if (!selectedFlow) return;
    dryRun.mutate({
      flow_name: selectedFlow,
      input_artifacts: selectedArtifacts,
      parameters: submittedParameters,
    });
  };

  const onSubmit = (): void => {
    if (!selectedFlow) return;
    submit.mutate(
      {
        flow_name: selectedFlow,
        input_artifacts: selectedArtifacts,
        parameters: submittedParameters,
      },
      {
        onSuccess: (data) => {
          toast.success(
            `Flow ${data.flow_name} submitted — dispatching ${data.node_count} node(s). ` +
              `Children appear in the project's runs as they start.`
          );
        },
        onError: (err) => {
          toast.error(`Flow submission failed: ${(err as Error).message}`);
        },
      }
    );
  };

  // The manifest is what tells us which inputs a flow declares, so a flow
  // whose manifest didn't load can't be submitted safely — the backend
  // would 400 on the missing inputs anyway.
  const manifestUnavailable = Boolean(selectedFlow) && !flowInputs;
  const canSubmit =
    Boolean(selectedFlow) &&
    !manifestUnavailable &&
    missingRequiredInputs.length === 0 &&
    !submit.isPending;
  // The dry run is available under exactly the same conditions as the
  // submission, and never after one: the two buttons take the same body, so a
  // dry run that could be pressed when Submit could not would be answering a
  // question about a request the page would refuse to send.
  const canDryRun = canSubmit && !dryRun.isPending;

  // What the readiness line may claim, and no more. `dryRun.data` is cleared
  // by the reset above whenever the body changes, so a verdict on screen is
  // always a verdict about what is on screen.
  const verdict: CompileVerdict = dryRun.isPending
    ? { kind: "pending" }
    : dryRun.data !== undefined
      ? {
          kind: "compiled",
          nodeCount: dryRun.data.node_count,
          fanoutNodeCount: dryRun.data.fanout_node_count,
          gatedCount: Object.keys(dryRun.data.gated_nodes).length,
        }
      : dryRun.error !== null
        ? { kind: "failed", message: (dryRun.error as Error).message }
        : { kind: "idle" };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Link to="/projects" className="hover:text-foreground">
          Projects
        </Link>
        <span aria-hidden>·</span>
        <Link to={`/projects/${project.data.slug}`} className="hover:text-foreground">
          {project.data.slug}
        </Link>
        <span aria-hidden>·</span>
        <Link to={`/projects/${project.data.slug}/submit`} className="hover:text-foreground">
          submit
        </Link>
        <span aria-hidden>·</span>
        <span>flow</span>
      </div>

      <PageHeader
        title="Submit a Flow"
        description="Pick a Flow template, wire its inputs from the catalog, and submit. Each node — and each fan-out child — runs as its own tracked run; the DAG below colors as they land."
      />

      <ExecutionModeNotice mode={executionMode.mode} isLoading={executionMode.isPending} />

      <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
        <section className="flex flex-col gap-3" aria-labelledby="flow-picker-heading">
          <h2 id="flow-picker-heading" className="label-micro text-muted-foreground">
            Templates
          </h2>
          <FlowPicker
            flows={flows.data}
            selected={selectedFlow}
            onSelect={onSelectFlow}
            isLoading={flows.isLoading}
            error={flows.isError ? (flows.error as Error) : null}
          />
        </section>

        <section className="flex flex-col gap-4" aria-labelledby="flow-preview-heading">
          <div className="flex items-center justify-between gap-2">
            <h2 id="flow-preview-heading" className="label-micro text-muted-foreground">
              Preview
            </h2>
            {selectedFlow && (
              <span className="font-mono text-xs text-muted-foreground">{selectedFlow}</span>
            )}
          </div>
          {/* The pipeline sits FIRST, across from the template list, because
              that is the choice it informs (flowstack#1398). It used to sit
              under `FlowManifestPreview`, which on `full_affdo_sequential` is
              one input and eight parameters with their full descriptions —
              about ten scroll-ticks of prose. So the reader picked a template,
              read every parameter of it, and only then saw what the flow
              actually does. The 2026-08-21 canvas puts the shape beside the
              templates for exactly this reason: it is the answer to "which of
              these did I want", and the parameters are the answer to a
              question you only reach once you have chosen. */}
          {selectedFlow && (
            <FlowStructurePanel
              flowName={selectedFlow}
              data={dag.data}
              isLoading={dag.isLoading}
              error={dag.isError ? (dag.error as Error) : null}
            />
          )}
          <Card className="p-card">
            <FlowManifestPreview
              manifest={manifest.data?.manifest}
              isLoading={manifest.isLoading && Boolean(selectedFlow)}
            />
          </Card>

          {selectedFlow && (
            <Card className="p-card">
              <h3 className="label-micro mb-3 text-muted-foreground">Inputs</h3>
              {manifestUnavailable ? (
                <EmptyState
                  title="Could not read this flow's inputs."
                  description={
                    manifest.isLoading
                      ? "Loading the flow manifest…"
                      : "The flow manifest failed to load, so its declared inputs are unknown."
                  }
                />
              ) : (
                <ArtifactInputPicker
                  inputs={flowInputs}
                  artifacts={artifacts.data ?? []}
                  selected={selectedArtifacts}
                  onChange={onSelectArtifact}
                  onUploadMolecule={(file, declaredCharge) =>
                    uploadMolecule.mutateAsync({ file, declaredCharge })
                  }
                  isLoading={artifacts.isLoading}
                />
              )}
              <SelectedInputPreview selected={selectedArtifacts} />
            </Card>
          )}
          {selectedFlow && (
            <Card className="p-card">
              <h3 className="label-micro mb-3 text-muted-foreground">Parameters</h3>
              {/* A manifest that failed to load would otherwise reach
               * ManifestForm as `undefined` and render "Pick a Flow first." at
               * someone who has already picked one. Same posture as Inputs. */}
              {manifestUnavailable && !manifest.isLoading ? (
                <EmptyState
                  title="Could not read this flow's parameters."
                  description="The flow manifest failed to load, so its declared parameters are unknown."
                />
              ) : (
                <ManifestForm
                  subject="Flow"
                  manifest={manifest.data?.manifest}
                  values={renderableEdits}
                  onChange={setParameterEdits}
                  isLoading={manifest.isLoading}
                />
              )}
            </Card>
          )}
          {/* fs#1797: the submission's outcome stays on the page, beside the
              button that produced it, with the way to the runs it created. The
              toast alone faded and led nowhere. `submit.data` is the LAST
              successful submission; a new one replaces it. */}
          {submit.data && (
            <FlowSubmittedNotice
              projectSlug={project.data.slug}
              flowName={submit.data.flow_name}
              flowRunId={submit.data.flow_run_id}
              nodeCount={submit.data.node_count}
            />
          )}
          <div className="flex items-center justify-end gap-3">
            {/* The canvas's pre-flight readiness line (#1282). It subsumes the
                bare "Select an artifact for: …" text that used to sit here —
                that clause is now one half of the same line, so the row never
                carries two overlapping statements about the same inputs. */}
            {selectedFlow && (
              <SubmitReadiness
                declaredInputCount={flowInputs ? Object.keys(flowInputs).length : undefined}
                missingInputs={missingRequiredInputs}
                nodeCount={dag.data?.nodes.length}
                verdict={verdict}
              />
            )}
            <Link
              to={`/projects/${project.data.slug}`}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Cancel
            </Link>
            {/* Beside Submit, as the canvas draws it — and secondary, because
                it is the cheap rehearsal of the expensive act next to it, not
                a competing one. It sends the same body to `/api/flows/dry-run`,
                which runs the submission's whole synchronous half and writes
                nothing; the verdict lands in the readiness line above rather
                than in a toast, because it is a statement about the thing you
                are still looking at. */}
            <Button
              variant="outline"
              onClick={onDryRun}
              disabled={!canDryRun}
              aria-disabled={!canDryRun}
              title={
                selectedFlow
                  ? missingRequiredInputs.length > 0
                    ? `Missing required input(s): ${missingRequiredInputs.join(", ")}`
                    : "Resolve the inputs and compile this Flow without dispatching it"
                  : "Pick a Flow template first"
              }
            >
              <FlaskConical className="mr-2 h-4 w-4" aria-hidden />
              {dryRun.isPending ? "Compiling…" : "Dry run"}
            </Button>
            <Button
              onClick={onSubmit}
              disabled={!canSubmit}
              aria-disabled={!canSubmit}
              title={
                selectedFlow
                  ? missingRequiredInputs.length > 0
                    ? `Missing required input(s): ${missingRequiredInputs.join(", ")}`
                    : "Submit this Flow"
                  : "Pick a Flow template first"
              }
            >
              <Send className="mr-2 h-4 w-4" aria-hidden />
              {submit.isPending ? "Submitting…" : "Submit Flow"}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}

/**
 * The Flow's STRUCTURE, in whichever of its two readings the user is on
 * (VIS/V2).
 *
 * It is a {@link VisualPanel} rather than the bare `Card` this used to be, and
 * that is the container contract rather than a repaint: the header anatomy, the
 * identifier slot and the caption/provenance slot are decided once in
 * `components/viz/VisualPanel.tsx`, and a second panel vocabulary grown here
 * would be one more thing to keep in step with the run workspace's.
 *
 * **The default is the phase lane wherever there is one.** Fifteen nodes and
 * thirty-seven edges render as a thumbnail whose labels are gone; the lane is
 * the same flow read as bands of repetition, and it is what a person picking a
 * template actually wants first. Where `derivePhaseLane` finds no phases to
 * read, this is the graph exactly as before, with no switch beside it.
 *
 * **The graph keeps its own full-window control.** `DAGCanvas` is handed no
 * `fullscreen` prop here, which is what leaves the boundary AND the ⤢ button in
 * the canvas's own chrome — the submit page has no pane to expand around it.
 * So one press reaches the graph and a second fills the window with it, which
 * is the capability VIS/V2 was told not to cost anybody.
 *
 * The maximise control on the PANEL is deliberately absent (`onToggleMaximise`
 * omitted, so none renders): a second expand affordance three pixels from the
 * canvas's own would be two buttons for one job.
 */
function FlowStructurePanel({
  flowName,
  data,
  isLoading,
  error,
}: {
  flowName: string;
  data: FlowDagResponse | FlowDagOverlayResponse | undefined;
  isLoading: boolean;
  error: Error | null;
}): JSX.Element {
  const [pickedShape, setPickedShape] = React.useState<FlowShapeView | null>(null);
  const lane = React.useMemo(
    () => (data === undefined ? null : derivePhaseLane(data.nodes, data.edges)),
    [data]
  );
  const shape: FlowShapeView = lane === null ? "graph" : (pickedShape ?? "lane");
  const showingLane = shape === "lane" && lane !== null;

  return (
    <VisualPanel
      as="section"
      label={`${flowName} structure`}
      // The switch NAMES the pane and CHANGES it, so it is the heading — the
      // case `VisualPanelProps.titleControl` exists for. With no lane there is
      // nothing to switch between and the pane says what it is in words.
      title={lane === null ? "DAG" : undefined}
      titleControl={
        lane === null ? undefined : <FlowShapeSwitch view={shape} onSelect={setPickedShape} />
      }
      identifier={flowName}
      controls={
        <>
          {/* Bulk retry surfaces only when the overlay supplies failed nodes;
              otherwise it returns null and stays out of the layout. */}
          <BulkRetryControl nodes={data?.nodes as DagNodeWithState[] | undefined} />
          <span className="label-micro text-muted-foreground">
            {data?.nodes.length ?? 0} nodes · {data?.edges.length ?? 0} edges
          </span>
        </>
      }
      caption={
        showingLane
          ? // Said because the lane's ×N marks are absent here and their absence
            // would otherwise read as "this flow does not fan out". A width is a
            // property of the molecule a run fans over — how many fragments, how
            // many torsions — so a template that has not been dispatched has
            // none, and printing a number from some earlier run of the same
            // template would be a figure about somebody else's ligand.
            "The template's shape. Fan-out width is counted per run, so it appears on a run's own workspace rather than here."
          : undefined
      }
    >
      {isLoading ? (
        <Skeleton className="h-[360px] w-full" />
      ) : error !== null ? (
        <EmptyState title="Could not load DAG." description={error.message} />
      ) : data === undefined ? null : showingLane ? (
        <PhaseLane lane={lane} label={`${flowName} pipeline`} />
      ) : (
        <React.Suspense fallback={<Skeleton className="h-[360px] w-full" />}>
          <DAGCanvas dag={data} />
        </React.Suspense>
      )}
    </VisualPanel>
  );
}
